import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageCircle, Save, RotateCcw, Eye, Sparkles, AlertCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { getBackendUrl } from './utils/api';

const API = `${getBackendUrl()}/api`;

const STATUS_COLOR = {
  pendente: 'from-blue-500 to-cyan-500',
  em_producao: 'from-orange-500 to-amber-500',
  pronto: 'from-emerald-500 to-green-500',
  entrega: 'from-violet-500 to-purple-500',
  entregue: 'from-teal-500 to-green-600',
  cancelado: 'from-rose-500 to-red-600',
};

const STATUS_ICON = {
  pendente: '✅',
  em_producao: '👨‍🍳',
  pronto: '🎉',
  entrega: '🛵',
  entregue: '💛',
  cancelado: '❌',
};

const StatusMessagesPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState({});
  const [originalTemplates, setOriginalTemplates] = useState({});
  const [variables, setVariables] = useState([]);
  const [labels, setLabels] = useState({});
  const [supportedStatuses, setSupportedStatuses] = useState([]);
  const [openStatus, setOpenStatus] = useState(null);
  const [previewCache, setPreviewCache] = useState({});
  const textareaRefs = useRef({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/whatsapp/status-messages`);
      setTemplates(res.data.templates || {});
      setOriginalTemplates(JSON.parse(JSON.stringify(res.data.templates || {})));
      setVariables(res.data.available_variables || []);
      setLabels(res.data.status_labels || {});
      setSupportedStatuses(res.data.supported_statuses || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isDirty = JSON.stringify(templates) !== JSON.stringify(originalTemplates);

  const updateStatus = (key, patch) => {
    setTemplates((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...patch },
    }));
    // Invalida preview cache do status alterado
    setPreviewCache((prev) => ({ ...prev, [key]: null }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await axios.put(`${API}/whatsapp/status-messages`, { templates });
      setTemplates(res.data.templates);
      setOriginalTemplates(JSON.parse(JSON.stringify(res.data.templates)));
      setPreviewCache({});
      toast.success('Mensagens salvas com sucesso!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleResetOne = (key) => {
    // Restaurar para o original (último salvo) — não para hardcoded default
    if (originalTemplates[key]) {
      updateStatus(key, originalTemplates[key]);
      toast.info('Mensagem revertida para o último estado salvo');
    }
  };

  const insertVariable = (statusKey, variable) => {
    const ref = textareaRefs.current[statusKey];
    const current = templates[statusKey]?.text || '';
    if (ref) {
      const start = ref.selectionStart;
      const end = ref.selectionEnd;
      const newText = current.slice(0, start) + variable + current.slice(end);
      updateStatus(statusKey, { text: newText });
      // Reposiciona cursor após inserir
      setTimeout(() => {
        ref.focus();
        const pos = start + variable.length;
        ref.setSelectionRange(pos, pos);
      }, 50);
    } else {
      updateStatus(statusKey, { text: current + variable });
    }
  };

  const loadPreview = async (statusKey) => {
    if (previewCache[statusKey]) return;
    try {
      const res = await axios.get(`${API}/whatsapp/status-messages/preview?status=${statusKey}`);
      setPreviewCache((prev) => ({ ...prev, [statusKey]: res.data.preview }));
    } catch (e) {
      console.warn('preview falhou:', e);
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-8 text-center text-gray-400">
        Carregando mensagens automáticas...
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="status-messages-panel">
      {/* Cabeçalho */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-emerald-400" />
            Mensagens Automáticas por Status
          </h2>
          <p className="text-sm text-gray-400 mt-1 max-w-2xl">
            Personalize o que seus clientes recebem pelo WhatsApp quando o status do pedido muda.
            Use variáveis como <code className="text-emerald-300 bg-emerald-900/20 px-1 rounded">{'{nome_cliente}'}</code> para
            mensagens personalizadas.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <span className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded-full">
              Alterações não salvas
            </span>
          )}
          <button
            data-testid="status-messages-save"
            onClick={handleSave}
            disabled={!isDirty || saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-green-700 hover:from-emerald-500 hover:to-green-600 text-white text-sm font-semibold shadow-lg shadow-emerald-900/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>

      {/* Card informativo */}
      <div className="rounded-xl border border-emerald-500/20 bg-emerald-950/20 p-3 flex items-start gap-2 text-xs">
        <AlertCircle className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
        <p className="text-emerald-200">
          Por padrão, só os status <strong>&quot;Pronto&quot;</strong> e <strong>&quot;Saiu para Entrega&quot;</strong> estão ativados, evitando
          encher o WhatsApp do cliente. Ative outros se fizer sentido pra sua operação.
        </p>
      </div>

      {/* Lista de status */}
      <div className="space-y-3">
        {supportedStatuses.map((key) => {
          const tpl = templates[key] || { enabled: false, text: '' };
          const label = labels[key] || key;
          const isOpen = openStatus === key;
          const gradient = STATUS_COLOR[key] || 'from-slate-500 to-gray-600';

          return (
            <div
              key={key}
              data-testid={`status-card-${key}`}
              className={`rounded-xl border-2 transition-all ${
                tpl.enabled
                  ? 'border-white/15 bg-slate-900/40'
                  : 'border-white/5 bg-slate-900/20 opacity-80'
              }`}
            >
              {/* Header colapsável */}
              <button
                onClick={() => setOpenStatus(isOpen ? null : key)}
                className="w-full flex items-center gap-3 p-4 text-left"
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center text-lg shadow-md flex-shrink-0`}>
                  {STATUS_ICON[key] || '📦'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white">{label}</span>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wide ${
                        tpl.enabled
                          ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                          : 'bg-slate-500/15 text-slate-400 border-slate-500/40'
                      }`}
                    >
                      {tpl.enabled ? 'Ativado' : 'Desativado'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 truncate max-w-md md:max-w-xl">
                    {tpl.text ? tpl.text.split('\n')[0].slice(0, 80) : '(sem mensagem)'}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {/* Toggle pill */}
                  <label
                    className="relative inline-flex items-center cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      data-testid={`status-toggle-${key}`}
                      type="checkbox"
                      className="sr-only peer"
                      checked={!!tpl.enabled}
                      onChange={(e) => updateStatus(key, { enabled: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-slate-700 rounded-full peer peer-checked:bg-emerald-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
                  </label>
                  {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </div>
              </button>

              {/* Corpo expandido */}
              {isOpen && (
                <div className="px-4 pb-4 space-y-3 border-t border-white/5">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-3">
                    {/* Editor */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-300">
                          Mensagem
                        </label>
                        <button
                          onClick={() => handleResetOne(key)}
                          title="Reverter alterações não salvas"
                          className="text-[11px] text-gray-400 hover:text-white flex items-center gap-1"
                        >
                          <RotateCcw className="w-3 h-3" />
                          Reverter
                        </button>
                      </div>
                      <textarea
                        data-testid={`status-textarea-${key}`}
                        ref={(el) => { textareaRefs.current[key] = el; }}
                        value={tpl.text || ''}
                        onChange={(e) => updateStatus(key, { text: e.target.value })}
                        placeholder="Digite a mensagem que o cliente receberá..."
                        rows={8}
                        maxLength={2000}
                        className="w-full glass-input font-mono text-sm leading-relaxed"
                      />
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-[10px] text-gray-500">
                          {(tpl.text || '').length}/2000 caracteres
                        </p>
                      </div>

                      {/* Variáveis disponíveis */}
                      <div className="mt-3">
                        <p className="text-[11px] font-medium text-gray-400 mb-1.5 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Inserir variável (clique para adicionar)
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {variables.map((v) => (
                            <button
                              key={v.key}
                              data-testid={`var-${key}-${v.key}`}
                              onClick={() => insertVariable(key, v.key)}
                              title={v.description}
                              className="text-[11px] font-mono px-2 py-1 rounded-md bg-indigo-500/10 hover:bg-indigo-500/25 text-indigo-200 border border-indigo-500/30 transition-colors"
                            >
                              {v.key}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Preview */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-300 flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          Preview (dados de exemplo)
                        </label>
                        <button
                          data-testid={`status-preview-btn-${key}`}
                          onClick={() => { setPreviewCache((p) => ({ ...p, [key]: null })); loadPreview(key); }}
                          className="text-[11px] text-emerald-300 hover:text-emerald-200"
                        >
                          Atualizar preview
                        </button>
                      </div>
                      <div className="rounded-xl bg-[#0a0e0a] border border-emerald-500/20 p-3 min-h-[210px]">
                        <div className="rounded-xl bg-[#005c4b] p-3 max-w-[280px] text-sm text-white shadow-md whitespace-pre-wrap break-words ml-auto">
                          {previewCache[key] === null ? (
                            <span className="text-emerald-100/60 text-xs italic">Clique em &quot;Atualizar preview&quot;...</span>
                          ) : previewCache[key] === undefined ? (
                            <button onClick={() => loadPreview(key)} className="text-emerald-200 underline text-xs">
                              Carregar preview
                            </button>
                          ) : (
                            renderWhatsAppText(previewCache[key])
                          )}
                        </div>
                        <p className="text-[10px] text-gray-500 mt-2 text-center">
                          Visualização aproximada de como aparece no WhatsApp
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Barra fixa de salvar (sticky) */}
      {isDirty && (
        <div className="sticky bottom-3 z-30 flex justify-center">
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="status-messages-save-sticky"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-emerald-600 to-green-700 hover:from-emerald-500 hover:to-green-600 text-white text-sm font-bold shadow-2xl shadow-emerald-900/50 disabled:opacity-50 transition-all"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </button>
        </div>
      )}
    </div>
  );
};

// Renderiza marcação simples do WhatsApp (*negrito*, _itálico_)
function renderWhatsAppText(text) {
  if (!text) return null;
  // Converte *bold* para <strong>
  const parts = text.split(/(\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('*') && part.endsWith('*')) {
      return <strong key={i} className="font-semibold">{part.slice(1, -1)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export default StatusMessagesPanel;
